<template>
  <div class="mentions-legales-page">
    <!-- Header with gradient -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Privacy policy and terms of use</h1>
        <p class="subtitle">Last updated : January 1, 2025</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Contact Section -->
      <section class="card contact-card">
        <div class="card-content">
          <h2 class="section-title">Contact</h2>
          <div class="text-content">
            <p>
              For any questions or concerns regarding our privacy policy or our 
              terms of use, please contact us:
            </p>
            <p class="contact-email">
              <strong>Email:</strong> <router-link to="/contact" class="link">via our form</router-link>
            </p>
            <p class="contact-email">
              <strong>Phone:</strong> <img src="/img/phone-number.png" alt="Arynae phone number" class="phone-img" />
            </p>
          </div>
          <p> or at the following address:</p>
          <div class="contact-logo">
            <img src="/img/mentions-legales.jpg" alt="Privacy policy" class="logo-img" />
          </div>
        </div>
      </section>

      <!-- Privacy policy -->
      <section class="card policy-card">
        <div class="card-content">
          <h2 class="section-title">Privacy policy</h2>
          
          <h3 class="subsection-title">1. Data collection</h3>
          <div class="text-content">
            <p>
              We collect different types of information to provide and improve our service:
            </p>
            <ul class="feature-list">
              <li><strong>Personal information:</strong> Name, email address, phone number, payment information.</li>
              <li><strong>Usage data:</strong> Information on how you access and use our service.</li>
              <li><strong>Cookies and tracking technologies:</strong> Use of cookies and similar technologies to track activity on our service.</li>
            </ul>
          </div>

          <h3 class="subsection-title">2. Data usage</h3>
          <div class="text-content">
            <p>
              We use the collected data for various purposes:
            </p>
            <ul class="feature-list">
              <li><strong>Service provision:</strong> To provide, maintain, and improve our service.</li>
              <li><strong>Communications:</strong> To send you important notifications, updates, and relevant information.</li>
              <li><strong>SMS Communications:</strong> To send you service notifications, updates, or requested information via text message. Message frequency varies. Message and data rates may apply. Reply STOP to opt-out at any time.</li>
              <li><strong>Marketing:</strong> To inform you about our offers, services, and promotions.</li>
            </ul>
          </div>

          <h3 class="subsection-title">3. Data sharing</h3>
          <div class="text-content">
            <p>
              Your information may be shared with:
            </p>
            <ul class="feature-list">
              <li><strong>Service providers:</strong> Third parties that help us provide and improve our service.</li>
              <li><strong>Legal requirements:</strong> To comply with laws or respond to legitimate government requests.</li>
            </ul>
            <p>
              <strong>SMS Opt-In Data Exclusion:</strong> Notwithstanding the above, mobile information will not be shared with third parties or affiliates for marketing or promotional purposes. All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
            </p>
          </div>

          <h3 class="subsection-title">4. Data protection</h3>
          <div class="text-content">
            <p>
              We implement security measures to protect your information:
            </p>
            <ul class="feature-list">
              <li><strong>Encryption:</strong> Use of security protocols such as SSL to protect data in transit.</li>
              <li><strong>Restricted access:</strong> Limited access to personal information for authorized employees and service partners.</li>
            </ul>
          </div>

          <h3 class="subsection-title">5. User rights</h3>
          <div class="text-content">
            <p>
              You have rights regarding your personal information:
            </p>
            <ul class="feature-list">
              <li><strong>Access:</strong> You can request a copy of the information we hold about you.</li>
              <li><strong>Correction:</strong> You can request the correction of any incorrect information.</li>
              <li><strong>Deletion:</strong> You can request the deletion of your personal information.</li>
            </ul>
            <p>
              To exercise these rights, please contact us at the address indicated below or 
              <router-link to="/contact" class="link">via our form</router-link>.
            </p>
          </div>

          <h3 class="subsection-title">6. Cookies and tracking technologies</h3>
          <div class="text-content">
            <p>We use cookies, web beacons and similar technologies to collect the following data related to usage and device when you access our website and use our services:</p>
            <ul class="feature-list">
              <li>Web pages visited, time of access, frequency and duration of visits, links clicked, interaction with advertisements.</li>
              <li>User segment or category.</li>
              <li>IP address, device model or type, operating system and version, browser type and settings.</li>
              <li>Identifiers (device identifier, advertising identifier, individual device token).</li>
              <li>Cookie-related data (e.g., cookie identifier).</li>
            </ul>
          </div>

          <h3 class="subsection-title">7. Third-party services</h3>
          <div class="text-content">
            <p>We use Stripe for payments, analytics and other business services. Stripe collects and processes personal data, including identifying information about devices that connect to its services via cookies and similar technologies. Stripe uses this information to operate and improve the services it provides to us, including for fraud detection, loss prevention, authentication and analytics. You can learn more about Stripe and its processing activities via their privacy policy at <a href="https://stripe.com/privacy" class="link">https://stripe.com/privacy</a>.</p>
          </div>

          <h3 class="subsection-title">8. Data retention</h3>
          <div class="text-content">
            <p>Unless required by law (e.g., legal retention obligations), we store personal data only as long as necessary to serve the purpose for which it was collected.</p>
          </div>

          <h3 class="subsection-title">9. Children's privacy</h3>
          <div class="text-content">
            <p>Our services are not intended for minors. We do not knowingly collect personal information from persons under 18 years of age.</p>
          </div>
        </div>
      </section>

      <!-- Terms of Use -->
      <section class="card terms-card">
        <div class="card-content">
          <h2 class="section-title">Terms of use</h2>
          
          <h3 class="subsection-title">1. Service description</h3>
          <div class="text-content">
            <p>
              We provide a range of B2B and SaaS services designed to digitize, strengthen online presence, 
              improve efficiency and productivity of businesses.
            </p>              
          </div>

          <h3 class="subsection-title">2. Acceptable use</h3>
          <div class="text-content">
            <p>
              By using our service, you agree to comply with the following rules:
            </p>
            <ul class="feature-list">
              <li><strong>Legality:</strong> Do not use our service for illegal or unauthorized purposes.</li>
              <li><strong>Resource respect:</strong> Do not abuse resources or disrupt the service.</li>
            </ul>
          </div>

          <h3 class="subsection-title">3. Intellectual property</h3>
          <div class="text-content">
            <p>
              All content and materials available on our service are protected by intellectual property rights 
              and are the exclusive property of our company.
            </p>
          </div>

          <h3 class="subsection-title">4. Limitation of liability</h3>
          <div class="text-content">
            <p>The service and all information provided therein are provided "as is" and "as available" without any warranty. TO THE EXTENT PERMITTED BY LAW, WE EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. TO THE EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY OR PUNITIVE DAMAGES, OR FOR LOSS OF PROFITS OR REVENUES. OUR TOTAL LIABILITY FOR ANY CLAIM UNDER THESE TERMS IS LIMITED TO THE AMOUNT YOU PAID US TO USE THE SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM.</p>
          </div>

          <h3 class="subsection-title">5. Termination</h3>
          <div class="text-content">
            <p>
              We reserve the right to suspend or terminate your access to the service in case of violation 
              of these terms.
            </p>
          </div>

          <h3 class="subsection-title">6. Payments</h3>
          <div class="text-content">
            <p>
              For users of paid plans, payments must be made according to the conditions 
              specified at the time of registration. Subscriptions to our SaaS are:
            </p>
            <ul class="feature-list">
              <li>annual and payable on each annual subscription anniversary date.</li>
              <li>lifetime and paid once at registration.</li>
            </ul>
          </div>

          <h3 class="subsection-title">7. Modifications to terms</h3>
          <div class="text-content">
            <p>
              We may modify these terms at any time. The modifications will be effective upon 
              publication on this page.
            </p>
          </div>

          <h3 class="subsection-title">8. Applicable law</h3>
          <div class="text-content">
            <p>
              These terms will be governed and construed in accordance with the laws in force in our jurisdiction.
            </p>
          </div>

          <h3 class="subsection-title">9. Fees and taxes</h3>
          <div class="text-content">
            <p>Users of paid services will be charged the fees indicated in the relevant subscription plan. All fees are exclusive of taxes, which we will invoice as applicable. You agree to pay all applicable taxes, other than taxes based on our gross revenues or net income.</p>
          </div>

          <h3 class="subsection-title">10. Refund policy</h3>
          <div class="text-content">
            <p>Unless required by law, we are not obligated to provide a refund at any time or for any reason.</p>
          </div>

          <h3 class="subsection-title">11. Cancellation by us</h3>
          <div class="text-content">
            <p>In addition to the above, we reserve the right to unilaterally cancel the service if you fail to comply with these terms, if we detect unusual usage patterns or unauthorized uses, or in response to requests from law enforcement or other public bodies.</p>
          </div>

          <h3 class="subsection-title">12. Changes to the service</h3>
          <div class="text-content">
            <p>We constantly modify and improve our service. We may add or remove features, suspend or completely stop the service. We reserve the right to modify any subscription plan and/or adjust the price of the service at our sole and absolute discretion.</p>
          </div>

          <h3 class="subsection-title">13. Indemnification</h3>
          <div class="text-content">
            <p>You will indemnify and hold us harmless, as well as our respective directors, officers, employees and agents, against all claims, demands, actions, judgments, obligations, liabilities, costs, expenses, damages and losses incurred as a result of (a) your breach of any of these terms; (b) any actual or alleged violation of any intellectual property rights arising from your actions or omissions; and/or (c) your violation of any applicable law.</p>
          </div>

          <h3 class="subsection-title">14. Regulatory compliance</h3>
          <div class="text-content">
            <p>You represent and warrant that you are at all times in compliance with all applicable laws (including all applicable sanctions and export control regulations) regarding all activities conducted here as well as your use of the Service.</p>
          </div>
        </div>
      </section>



      <!-- Call to action -->
      <div class="cta-container">
        <router-link to="/contact" class="cta-button">Contact us for more information</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head'

export default {
  name: 'MentionsLegalesPage',
  setup() {
    useHead({
      title: 'Arynae - Privacy policy and terms of use',
      meta: [
        { name: 'description', content: 'Privacy policy and terms of use of Arynae. View our data protection practices and your rights regarding our B2B and SaaS services.' },
        { name: 'keywords', content: 'privacy policy, terms of use, GDPR, data protection, user rights, SaaS, data security, B2B' },
        { name: 'robots', content: 'index, nofollow' },
        { name: 'author', content: 'Arynae' },
        { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        // Essential Open Graph
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: 'Arynae | Privacy policy and terms of use' },
        { property: 'og:description', content: 'View our terms of use, data protection policy and your rights regarding our B2B and SaaS services.' },
        { property: 'og:url', content: 'https://www.arynae.com/en/mentions-legales' },
        { property: 'og:locale', content: 'en_US' },
        { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
        { property: 'og:image:alt', content:'Digital agency - No Code solutions, SaaS, Chatbots & SEO' },
        // Basic Twitter Card
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Arynae | Privacy policy and terms of use' },
        { name: 'twitter:description', content: 'View our terms of use, data protection policy and your rights regarding our B2B and SaaS services.' },
        { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
        { rel: 'canonical', href: 'https://www.arynae.com/en/mentions-legales' },
        { rel: 'alternate', hreflang: 'fr', href: 'https://www.arynae.com/mentions-legales' },
        { rel: 'alternate', hreflang: 'en', href: 'https://www.arynae.com/en/mentions-legales' }
      ],
      htmlAttrs: {
        lang: 'en'
      }
    })
    
    return {}
  }
}
</script>

<style scoped>
.mentions-legales-page {
  font-family: Arial, sans-serif;
  color: #333;
}

/* Header Styles */
.header {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  padding-bottom: 6rem;
  color: white;
}

.header-content {
  text-align: center;
  padding: 5rem 2rem 3rem;
}

.main-title {
  font-size: 2.75rem;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  font-style: italic;
}

/* Content Container */
.content-container {
  max-width: 1200px;
  margin: -3rem auto 4rem;
  padding: 0 2rem;
}

/* Card Styles */
.card {
  background: linear-gradient(to bottom, #ffffff, #f3f4f6);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-content {
  padding: 2rem;
}

.section-title {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
  position: relative;
}

.section-title:after {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #2563EB, #7C3AED);
}

.subsection-title {
  font-size: 1.35rem;
  margin: 2rem 0 1rem;
  color: #444;
}

.text-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
}

.text-content p {
  margin-bottom: 1rem;
}

.contact-logo {
  display: flex;
  justify-content: center;
  margin: 2rem 0;
}

.logo-img {
  height: 96px;
}

.contact-email {
  margin-top: 1rem;
}

.phone-img {
  vertical-align: middle;
}

.link {
  color: #3B82F6;
  text-decoration: none;
  transition: color 0.3s;
}

.link:hover {
  color: #2563EB;
  text-decoration: underline;
}

/* Feature Lists */
.feature-list {
  list-style: none;
  padding-left: 0;
  margin: 1.5rem 0;
}

.feature-list li {
  position: relative;
  padding-left: 1.75rem;
  margin-bottom: 1rem;
}

.feature-list li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #2563EB;
  font-weight: bold;
}

/* Call to Action */
.cta-container {
  text-align: center;
  margin: 3rem 0;
}

.cta-button {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 2rem;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: inline-block;
  text-decoration: none;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .header-content {
    padding: 4rem 1rem 2rem;
  }
  
  .main-title {
    font-size: 2rem;
  }
  
  .content-container {
    padding: 0 1rem;
  }
  
  .card-content {
    padding: 1.5rem;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
  
  .subsection-title {
    font-size: 1.25rem;
  }
  
  .cta-button {
    font-size: 1.1rem;
    padding: 0.875rem 2rem;
  }
}
</style>